import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { AdmPlan } from "@custom-types/base-types";
import { BaseProduct } from "@utils/product/product-utils-types";
import { AllSphereXg2026Bundles } from "@utils/product/product-types";
import { isHoloBuilderLegacyPlanIdentifier } from "@utils/type-guards";
import {
  grantsAnyBundle,
  grantsFeature,
  HB_TO_SXG_MIGRATED_IDENTIFIER,
  isSubscriptionPlan,
  isTokenPlan,
} from "@utils/plan-utils";

/** Feature identifier that opens the Sphere XG dashboard instead of the HoloBuilder one */
const SPHERE_DASHBOARD_IDENTIFIER: FaroCoreApiTypes.FeatureIdentifier =
  "sphere-dashboard";

/** The applications a plan can grant access to */
export const PLAN_APPS = ["holoBuilder", "sphereXg"] as const;
export type PlanApp = (typeof PLAN_APPS)[number];

/** Whether the value is one of the applications a plan can grant access to */
export function isPlanApp(value: string): value is PlanApp {
  return (PLAN_APPS as readonly string[]).includes(value);
}

/** Display name of every application */
export const PLAN_APP_NAMES: Record<PlanApp, string> = {
  holoBuilder: "HoloBuilder",
  sphereXg: "Sphere XG",
};

/** The application settings that are attached to the subscription of a plan */
export interface PlanAppSettings {
  /** The application that opens the 360° images of the plan */
  defaultViewerApp: PlanApp;

  /** The dashboard that is opened after the members of the plan log in */
  defaultDashboardApp: PlanApp;
}

/** The applications a plan opens unless the user picks different ones */
export const DEFAULT_PLAN_APP_SETTINGS: PlanAppSettings = {
  defaultViewerApp: "holoBuilder",
  defaultDashboardApp: "sphereXg",
};

/** The applications the legacy HoloBuilder plans open, as they predate Sphere XG on both sides */
const LEGACY_PLAN_APP_SETTINGS: PlanAppSettings = {
  defaultViewerApp: "holoBuilder",
  defaultDashboardApp: "holoBuilder",
};

/** The applications of the plans that never open in HoloBuilder at all */
const SPHERE_XG_ONLY_PLAN_APP_SETTINGS: PlanAppSettings = {
  defaultViewerApp: "sphereXg",
  defaultDashboardApp: "sphereXg",
};

/**
 * @returns whether a plan of the given product lets the user pick its applications. The settings
 * choose between HoloBuilder and Sphere XG, so a product that never opens in HoloBuilder, such as
 * a Point Cloud plan without 360°, has no choice to offer.
 * @param product the product the plan is created from
 */
export function offersPlanAppChoice(product: BaseProduct): boolean {
  return product.apps.includes("holoBuilder");
}

/**
 * @returns the applications a plan of the given product opens unless the user picks different ones
 * @param product the product the plan is created from
 */
export function defaultPlanAppSettingsOf(
  product: BaseProduct
): PlanAppSettings {
  return isHoloBuilderLegacyPlanIdentifier(product.identifier)
    ? LEGACY_PLAN_APP_SETTINGS
    : DEFAULT_PLAN_APP_SETTINGS;
}

/**
 * @returns the dashboard the plan actually opens. A Sphere XG viewer redirects every link
 * pointing to HoloBuilder, so the dashboard moves along with it and is no longer offered.
 * @param settings the application settings the user picked
 */
export function effectiveDashboardApp({
  defaultViewerApp,
  defaultDashboardApp,
}: PlanAppSettings): PlanApp {
  return defaultViewerApp === "sphereXg" ? "sphereXg" : defaultDashboardApp;
}

/**
 * @returns the features that apply the application settings to the created subscription
 * @param settings the application settings the user picked
 */
export function planAppFeatures(
  settings: PlanAppSettings
): FaroCoreApiTypes.FeatureIdentifier[] {
  const features: FaroCoreApiTypes.FeatureIdentifier[] = [];

  if (settings.defaultViewerApp === "sphereXg") {
    features.push(HB_TO_SXG_MIGRATED_IDENTIFIER);
  }
  if (effectiveDashboardApp(settings) === "sphereXg") {
    features.push(SPHERE_DASHBOARD_IDENTIFIER);
  }

  return features;
}

/**
 * Whether the plan is one of the subscriptions that only ever open in Sphere XG: the 2026 Scan
 * plans, and the 2025 plans sold with the Point Cloud addon but without the 360° one. They are
 * recognized by their bundles, as they carry no application feature of their own.
 */
function isSphereXgOnlyPlan(plan: AdmPlan): boolean {
  const is2026ScanPlan = grantsAnyBundle(plan, AllSphereXg2026Bundles);
  const isPointCloudWithout360 =
    grantsAnyBundle(plan, ["sphere-xg-addon-point-cloud"]) &&
    !grantsAnyBundle(plan, ["sphere-xg-addon-360"]);

  return is2026ScanPlan || isPointCloudWithout360;
}

/**
 * @returns the applications an existing plan opens, or null for plans that open none of them,
 * such as add-ons and credits
 * @param plan the plan to read the granted features of
 */
export function planAppSettingsOf(plan: AdmPlan): PlanAppSettings | null {
  if (!isSubscriptionPlan(plan) || isTokenPlan(plan)) {
    return null;
  }

  if (isSphereXgOnlyPlan(plan)) {
    return SPHERE_XG_ONLY_PLAN_APP_SETTINGS;
  }

  const granted: PlanAppSettings = {
    defaultViewerApp: grantsFeature(plan, HB_TO_SXG_MIGRATED_IDENTIFIER)
      ? "sphereXg"
      : "holoBuilder",
    defaultDashboardApp: grantsFeature(plan, SPHERE_DASHBOARD_IDENTIFIER)
      ? "sphereXg"
      : "holoBuilder",
  };

  return {
    ...granted,
    defaultDashboardApp: effectiveDashboardApp(granted),
  };
}
